import React, { useEffect, useRef, useState, useContext } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { AuthContext } from '../context/AuthContext';

export default function Amplification() {
  const { user } = useContext(AuthContext) || {};
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');
  const [sourceUrl, setSourceUrl] = useState('');
  const [gainDb, setGainDb] = useState(3);
  const [preset, setPreset] = useState('Custom');
  const [presets, setPresets] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // 0-100 or null
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [resultDuration, setResultDuration] = useState(null);
  const [resultCacheKey, setResultCacheKey] = useState(null); // used to bust cache for result playback
  const [extreme, setExtreme] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [ampCfg, setAmpCfg] = useState({ defaultGainDb: 3, maxGainDb: 12, maxGainDbExtreme: 24 });
  const [presetName, setPresetName] = useState('');
  const [presetGain, setPresetGain] = useState(3);
  const [presetExtreme, setPresetExtreme] = useState(false);
  const resultAudioRef = useRef(null);
  const userAdjustedGain = useRef(false);
  // Transcription state
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [transcriptError, setTranscriptError] = useState('');
  // Clip selection
  const [myUploads, setMyUploads] = useState([]);
  const [showClipSelector, setShowClipSelector] = useState(false);

  // Minimal gated debug logger
  const dbg = (...args) => {
    try {
      if (typeof window !== 'undefined' && window.__AMP_DEBUG__) {
        // eslint-disable-next-line no-console
        console.log('[AMP]', ...args);
      }

  async function onTranscribe() {
    if (!result?.url) return;
    setTranscribing(true);
    setTranscriptError('');
    try {
      const r = await api.transcribe({ sourceUrl: result.url });
      if (r?.item?.id) {
        const tid = r.item.id;
        const g = await api.getTranscript(tid);
        if (g?.item) {
          setTranscript(g.item);
          pushToast({ type: 'success', text: 'Transcription complete' });
        } else {
          const msg = g?.error || 'Failed to fetch transcript';
          setTranscriptError(msg);
          pushToast({ type: 'error', text: msg });
        }
      } else {
        const msg = r?.error || 'Transcription unavailable';
        setTranscriptError(msg);
        pushToast({ type: 'error', text: msg });
      }
    } catch (e) {
      setTranscriptError('Transcription failed');
      pushToast({ type: 'error', text: 'Transcription failed' });
    } finally {
      setTranscribing(false);
    }
  }
    } catch {}
  };

  const Spinner = () => (
    <span style={{ display: 'inline-block', width: 14, height: 14, marginRight: 6 }}>
      <svg viewBox="0 0 50 50" style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }}>
        <circle cx="25" cy="25" r="20" stroke="#9ca3af" strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray="31.4 188.4" />
      </svg>
      <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </span>
  );

  async function loadMyUploads() {
    try {
      const r = await api.listMyUploads({ limit: 20, sort: 'createdAt:desc' });
      if (Array.isArray(r?.items)) {
        setMyUploads(r.items);
      }
    } catch (e) {
      console.error('Failed to load uploads:', e);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const p = await api.getAmplifyPresets();
        if (Array.isArray(p?.items)) setPresets(p.items);
      } catch {}
      try {
        const r = await api.listRecentAmplified({ limit: 5 });
        if (Array.isArray(r?.items)) setRecent(r.items);
      } catch {}
      // Load user uploads
      loadMyUploads();
      try {
        // Try session cache first
        dbg('init: reading cache amplifyConfigCache');
        const cached = safeSessionStorageGet('amplifyConfigCache');
        if (cached) {
          dbg('cache hit amplifyConfigCache');
          const cfg = parseAmpCfg(cached);
          setAmpCfg(cfg);
          if (!userAdjustedGain.current) {
            dbg('apply cached defaultGainDb', cfg.defaultGainDb);
            setGainDb(cfg.defaultGainDb);
          } else {
            dbg('skip applying cached defaultGainDb due to userAdjustedGain');
          }
        } else {
          dbg('cache miss amplifyConfigCache');
        }
        // Always fetch fresh (auth-protected). On success, update cache.
        dbg('fetching getAmplifyConfig()');
        const c = await api.getAmplifyConfig();
        const cfg = parseAmpCfg(c?.item || {});
        dbg('fetch ok getAmplifyConfig()', cfg);
        setAmpCfg(cfg);
        safeSessionStorageSet('amplifyConfigCache', JSON.stringify(cfg));
        if (!userAdjustedGain.current) {
          dbg('apply fetched defaultGainDb', cfg.defaultGainDb);
          setGainDb(cfg.defaultGainDb);
        } else {
          dbg('skip applying fetched defaultGainDb due to userAdjustedGain');
        }
      } catch (err) {
        dbg('config init error', err);
      }
    })();
  }, []);

  // Prefill from query params
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const s = params.get('sourceUrl');
      const p = params.get('preset');
      const g = params.get('gainDb');
      if (s) setSourceUrl(s);
      if (p) setPreset(p);
      if (g && !Number.isNaN(Number(g))) {
        setGainDb(Number(g));
        userAdjustedGain.current = true;
      }
    } catch {}
  }, []);

  // Clamp gain when config or extreme mode changes
  useEffect(() => {
    const max = extreme ? ampCfg.maxGainDbExtreme : ampCfg.maxGainDb;
    const n = Number(gainDb);
    if (!Number.isFinite(n)) return;
    if (n > max) {
      dbg('clamp gain down', { from: n, to: max, extreme });
      setGainDb(max);
    }
    if (n < 0) {
      dbg('clamp gain up', { from: n, to: 0, extreme });
      setGainDb(0);
    }
  }, [ampCfg.maxGainDb, ampCfg.maxGainDbExtreme, extreme]);

  async function onAmplify(e) {
    e.preventDefault();
    setError('');
    setProcessing(true);
    setResult(null);
    setResultDuration(null);
    try {
      if (extreme) {
        // Confirm with the user about potential clipping/distortion
        const ok = window.confirm('Extreme amplification can cause clipping, distortion, and hearing risk. Proceed?');
        if (!ok) { setProcessing(false); return; }
      }
      const res = await api.amplifyAudio({ sourceUrl, gainDb: Number(gainDb), preset, extreme: !!extreme });
      if (res?.item?.url) {
        setResult(res.item);
        setResultCacheKey(Date.now());
        // Reset transcript when a new result is generated
        setTranscript(null);
        setTranscriptError('');
        pushToast({ type: 'success', text: 'Amplification complete' });
      } else {
        const msg = res?.error || 'Failed to amplify';
        setError(msg);
        pushToast({ type: 'error', text: msg });
      }
    } catch (err) {
      const msg = err?.message || 'Amplify request failed';
      setError(msg);
      pushToast({ type: 'error', text: msg });
    } finally {
      setProcessing(false);
    }
  }

  async function onPickFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await api.uploadFile(file, (pct) => setUploadProgress(pct));
      if (res?.item?.url) {
        setSourceUrl(res.item.url);
        pushToast({ type: 'success', text: 'Upload complete' });
      } else {
        const msg = res?.error || 'Upload failed';
        setError(msg);
        pushToast({ type: 'error', text: msg });
      }
    } catch (err) {
      setError('Upload failed');
      pushToast({ type: 'error', text: 'Upload failed' });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(null), 500);
      // reset input value so the same file can be re-selected if needed
      e.target.value = '';
    }
  }

  function pushToast(t) {
    const id = Math.random().toString(36).slice(2);
    const item = { id, ...t };
    setToasts(prev => [...prev, item]);
    setTimeout(() => { setToasts(prev => prev.filter(x => x.id !== id)); }, 3500);
  }

  return (
    <div style={{ padding: 16 }}>
      <h2>Amplification</h2>
      <p style={{ color: '#9ca3af' }}>Make your audio pop. Boost volume, pick a preset, and export.</p>
      <form onSubmit={onAmplify} style={{ display: 'grid', gap: 8, maxWidth: 640 }}>
        <input id="amplify-source-url" name="sourceUrl" placeholder="Source URL (e.g., /uploads/file.mp3)" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} style={inputStyle} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input id="amplify-file" name="file" type="file" accept="audio/*,video/*" onChange={onPickFile} />
          <span style={{ color: '#9ca3af', fontSize: 12 }}>
            {uploading ? 'Uploading…' : 'Select a file to upload'}
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <button 
            type="button"
            onClick={() => setShowClipSelector(!showClipSelector)}
            style={{ 
              background: '#374151', 
              color: '#e5e7eb', 
              border: 0, 
              borderRadius: 8, 
              padding: '8px 12px', 
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {showClipSelector ? '✕ Hide' : '📁 Select from My Uploads'} ({myUploads.length})
          </button>
        </div>
        {showClipSelector && (
          <div style={{ 
            marginTop: 12, 
            padding: 12, 
            background: '#0f172a', 
            border: '1px solid #374151', 
            borderRadius: 8,
            maxHeight: 300,
            overflowY: 'auto'
          }}>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 8px 0' }}>
              Click a clip to use it:
            </p>
            {myUploads.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: 14 }}>No uploads found. Upload a file first.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {myUploads.map((upload) => (
                  <button
                    key={upload._id}
                    type="button"
                    onClick={() => {
                      setSourceUrl(upload.url);
                      setShowClipSelector(false);
                      pushToast({ type: 'success', text: `Selected: ${upload.filename}` });
                    }}
                    style={{
                      background: sourceUrl === upload.url ? '#ff5a1f' : '#1f2937',
                      color: '#e5e7eb',
                      border: sourceUrl === upload.url ? '2px solid #ff7a45' : '1px solid #374151',
                      borderRadius: 6,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (sourceUrl !== upload.url) {
                        e.target.style.background = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sourceUrl !== upload.url) {
                        e.target.style.background = '#1f2937';
                      }
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                      {upload.filename || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      {upload.mimetype} • {upload.size ? `${(upload.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                      {upload.createdAt && ` • ${new Date(upload.createdAt).toLocaleDateString()}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <label htmlFor="amplify-preset" style={{ color: '#e5e7eb' }}>Preset</label>
          <select id="amplify-preset" name="preset" value={preset} onChange={e => setPreset(e.target.value)} style={selectStyle}>
            {presets.map(p => <option key={p.name} value={p.name}>{p.name} (+{p.gainDb}dB)</option>)}
            {presets.length === 0 && <option value="Custom">Custom</option>}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input id="amplify-extreme" name="extreme" type="checkbox" checked={extreme} onChange={(e) => {
            const checked = e.target.checked;
            setExtreme(checked);
            if (checked && Number(gainDb) < 13) setGainDb(18);
          }} />
          <label htmlFor="amplify-extreme" style={{ color: '#e5e7eb' }}>Extreme amplification</label>
          {extreme && <span style={{ color: '#fca5a5', fontSize: 12 }}>(Warning: may clip, distort, or damage hearing. Lower volume before playback.)</span>}
        </div>
        <label htmlFor="amplify-gain" style={{ color: '#e5e7eb' }} data-testid="amp-label">Loudness (+dB)</label>
        <input
          id="amplify-gain"
          name="gainDb"
          type="range"
          min="0"
          max={extreme ? ampCfg.maxGainDbExtreme : ampCfg.maxGainDb}
          step="1"
          value={gainDb}
          onInput={e => { userAdjustedGain.current = true; dbg('user gain onInput', e.target.value); setGainDb(e.target.value); }}
          onChange={e => { userAdjustedGain.current = true; dbg('user gain onChange', e.target.value); setGainDb(e.target.value); }}
          data-testid="amp-gain"
        />
        <div style={{ color: '#9ca3af' }}>
          <span data-testid="amp-gain-text">Gain: +{gainDb} dB {extreme && '(extreme)'} • Clamp: 0–{extreme ? ampCfg.maxGainDbExtreme : ampCfg.maxGainDb} dB</span>
          <span data-testid="amp-default" style={{ marginLeft: 8, color: '#6b7280' }}>(Default: +{ampCfg.defaultGainDb} dB; Extreme max: {ampCfg.maxGainDbExtreme} dB)</span>
        </div>
        {error ? (
          <div style={{ background: '#2b0d0d', border: '1px solid #7f1d1d', color: '#fecaca', borderRadius: 8, padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{String(error)}</span>
            <button onClick={onAmplify} style={{ marginLeft: 'auto', background: '#ef4444', color: '#0b0b0e', border: 0, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Retry</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button disabled={processing || uploading || !sourceUrl} style={btnPrimary}>{processing ? 'Processing…' : 'Amplify'}</button>
            <span style={{ color: '#9ca3af', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              {(uploading || processing) && <Spinner />}
              {uploading ? 'Status: Uploading…' : processing ? 'Status: Processing…' : result ? 'Status: Done' : 'Status: Idle'}
            </span>
          </div>
        )}
        {(uploading || processing) && (
          uploadProgress !== null && uploading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ height: 6, flex: 1, background: '#111827', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#ff5a1f', transition: 'width 120ms ease' }} />
              </div>
              <span style={{ color: '#9ca3af', fontSize: 12 }}>{uploadProgress}%</span>
            </div>
          ) : (
            <div style={{ height: 6, width: '100%', background: '#111827', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '40%', background: '#ff5a1f', animation: 'indet 1.2s ease-in-out infinite' }} />
              <style>{`@keyframes indet { 0%{ transform: translateX(-100%);} 50%{ transform: translateX(50%);} 100%{ transform: translateX(200%);} }`}</style>
            </div>
          )
        )}
      </form>

      {result && (
        <div style={{ marginTop: 24 }}>
          <h3>Result</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {(() => {
              const baseUrl = resolveMediaUrl(result?.url);
              const playUrl = addCacheBuster(baseUrl, resultCacheKey);
              return (
                <>
                  <audio
                    ref={resultAudioRef}
                    onLoadedMetadata={(e) => setResultDuration(e?.target?.duration)}
                    onError={() => {
                      // If cache or stale response issues, bump cache key to retry once
                      setResultCacheKey((k) => (k ? k + 1 : Date.now()));
                    }}
                    crossOrigin="anonymous"
                    controls
                    src={playUrl}
                    preload="metadata"
                  />
                  <a href={playUrl} download style={{ background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '6px 10px' }}>Download</a>
                  <button onClick={onTranscribe} disabled={transcribing} style={{ background: '#4b5563', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '6px 10px' }}>{transcribing ? 'Transcribing…' : 'Transcribe'}</button>
                </>
              );
            })()}
            <span style={{ color: '#9ca3af' }}>
              Preset: {result.preset} • +{result.gainDb}dB {result.fallback ? `(fallback: ${result.fallback})` : ''}
              {typeof resultDuration === 'number' && isFinite(resultDuration) ? ` • Duration: ${Math.floor(resultDuration/60)}:${String(Math.floor(resultDuration%60)).padStart(2,'0')}` : ''}
            </span>
            <button onClick={onAmplify} style={{ marginLeft: 'auto', background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Re-run</button>
          </div>
          {(transcriptError || transcript) && (
            <div style={{ marginTop: 12, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 10 }}>
              {transcriptError && <div style={{ color: '#fca5a5' }}>Transcription error: {String(transcriptError)}</div>}
              {transcript && (
                <div>
                  <div style={{ color: '#9ca3af', marginBottom: 6 }}>
                    Language: {transcript.language || 'n/a'} • Confidence: {transcript.confidence != null ? String(transcript.confidence) : 'n/a'}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#e5e7eb' }}>{transcript.text || ''}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent Amplifications */}
      <div style={{ marginTop: 32 }}>
        <h3>Recent Amplifications</h3>
        {recent.length === 0 ? (
          <div style={{ color: '#9ca3af' }}>No recent items</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
            {recent.map((it) => {
              const playUrl = resolveMediaUrl(it?.url);
              return (
                <li key={it.url} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
                  <audio src={playUrl} controls preload="none" style={{ maxWidth: 260 }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ color: '#e5e7eb' }}>{it.preset || 'Custom'} • +{it.gainDb}dB{it.fallback ? ` • ${it.fallback}` : ''}</span>
                    <button onClick={() => setSourceUrl(it.url)} style={{ alignSelf: 'start', background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', marginTop: 6 }}>Use as source</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Toasts */}
      <div style={{ position: 'fixed', right: 16, bottom: 16, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 9999 }}>
        {toasts.map(t => (
          <div key={t.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: t.type === 'error' ? '#3f1d1d' : '#1f2937', color: t.type === 'error' ? '#fecaca' : '#e5e7eb', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
            {t.text}
          </div>
        ))}
      </div>

      {/* Preset Manager (Admin only) */}
      {isAdmin && (
        <div style={{ marginTop: 32 }}>
          <h3>Preset Manager (Admin)</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="Preset name"
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              style={inputStyle}
            />
            <input
              placeholder="Gain dB"
              type="number"
              value={presetGain}
              onChange={e => setPresetGain(e.target.value)}
              style={{ ...inputStyle, width: 140 }}
            />
            <label style={{ color: '#e5e7eb' }}>
              <input type="checkbox" checked={presetExtreme} onChange={e => setPresetExtreme(e.target.checked)} /> Extreme
            </label>
            <button
              onClick={async () => {
                try {
                  const name = presetName.trim();
                  const g = Number(presetGain);
                  if (!name) { pushToast({ type: 'error', text: 'Name required' }); return; }
                  if (!Number.isFinite(g)) { pushToast({ type: 'error', text: 'Gain must be a number' }); return; }
                  const res = await api.createAmplifyPreset({ name, gainDb: g, extreme: !!presetExtreme });
                  if (res?.ok !== true && res?.error) throw new Error(res.error);
                  pushToast({ type: 'success', text: 'Preset saved' });
                  // Refresh presets
                  const p = await api.getAmplifyPresets();
                  if (Array.isArray(p?.items)) setPresets(p.items);
                } catch (e) {
                  pushToast({ type: 'error', text: e?.message || 'Failed to save preset' });
                }
              }}
              style={btnPrimary}
            >Save/Update Preset</button>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, marginTop: 12, display: 'grid', gap: 8 }}>
            {presets.map(p => (
              <li key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
                <span style={{ color: '#e5e7eb' }}>{p.name} {p.gainDb != null ? `(+${p.gainDb}dB)` : ''} {p.extreme ? '• extreme' : ''}</span>
                {p.name !== 'Custom' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete preset ${p.name}?`)) return;
                      try {
                        const r = await api.deleteAmplifyPreset(p.name);
                        if (r?.ok !== true && r?.error) throw new Error(r.error);
                        pushToast({ type: 'success', text: 'Preset deleted' });
                        const after = await api.getAmplifyPresets();
                        if (Array.isArray(after?.items)) setPresets(after.items);
                      } catch (e) {
                        pushToast({ type: 'error', text: e?.message || 'Failed to delete preset' });
                      }
                    }}
                    style={{ ...btnPrimary, background: '#ef4444' }}
                  >Delete</button>
                )}
              </li>
            ))}
            {presets.length === 0 && (
              <li style={{ color: '#9ca3af' }}>No presets</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Resolve media URL for playback/download. If it's a backend-relative uploads path,
// prefix with API_BASE so the browser can fetch it regardless of the frontend host.
function resolveMediaUrl(u) {
  if (!u || typeof u !== 'string') return '';
  if (u.startsWith('/uploads/')) return `${API_BASE}${u}`;
  return u;
}

// Add a cache-busting query param when a cache key is provided.
function addCacheBuster(url, key) {
  if (!url) return '';
  if (!key) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(String(key))}`;
}

// Parse and validate amp config values with safe fallbacks
function parseAmpCfg(item) {
  const cfg = {
    defaultGainDb: Number.isFinite(Number(item?.defaultGainDb)) ? Number(item.defaultGainDb) : 3,
    maxGainDb: Number.isFinite(Number(item?.maxGainDb)) ? Number(item.maxGainDb) : 12,
    maxGainDbExtreme: Number.isFinite(Number(item?.maxGainDbExtreme)) ? Number(item.maxGainDbExtreme) : 24,
  };
  // Ensure consistency: extreme max >= normal max, defaults sane
  if (cfg.maxGainDbExtreme < cfg.maxGainDb) cfg.maxGainDbExtreme = cfg.maxGainDb;
  if (cfg.defaultGainDb > cfg.maxGainDb) cfg.defaultGainDb = cfg.maxGainDb;
  if (cfg.defaultGainDb < 0) cfg.defaultGainDb = 0;
  return cfg;
}

function safeSessionStorageGet(key) {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionStorageSet(key, val) {
  try { window.sessionStorage.setItem(key, val); } catch {}
}

const inputStyle = { width: '100%', background: '#0b0b0e', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: 8 };
const selectStyle = { background: '#0b0b0e', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: 8 };
const btnPrimary = { background: '#ff5a1f', color: '#fff', border: 0, borderRadius: 8, padding: '8px 12px' };
